'use client';

import { zodResolver } from '@hookform/resolvers/zod';
import { useRouter } from 'next/navigation';
import React from 'react';
import { useForm } from 'react-hook-form';
import { observer } from 'mobx-react-lite';
import type { z } from 'zod';

import { Button } from '@/components/ui/button';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { PhoneInput } from '@/components/ui/phone-input';
import { useToast } from '@/components/ui/use-toast';
import { otpLoginSchema } from '@/validations/GuestbookValidation';
import { useAuthStore } from '@/presentation/hooks/useStores';

const LoginForm = observer(() => {
  const toast = useToast();
  const router = useRouter();
  const authStore = useAuthStore();

  const loginForm = useForm<z.infer<typeof otpLoginSchema>>({
    resolver: zodResolver(otpLoginSchema),
    defaultValues: {
      phone: '',
    },
  });

  const onSubmit = async (values: z.infer<typeof otpLoginSchema>) => {
    try {
      // Step 1: Phone Lookup (according to docs/auth.md)
      // const phoneCheck = await authStore.lookupPhone(values.phone);
      // if (!phoneCheck.exists) {
      //     toast.toast({
      //         title: 'Phone number not found',
      //         description: 'This phone number is not registered. Please sign up first.',
      //         variant: 'destructive',
      //     });
      //     return;
      // }

      // Step 2: Request OTP for login (according to docs/auth.md)
      await authStore.requestOTPNew(values.phone, 'LOGIN');

      // Store phone number for OTP verification
      sessionStorage.setItem('phone', values.phone);

      toast.toast({
        title: 'OTP Sent',
        description: 'Please check your phone for the verification code.',
      });

      // Navigate to OTP verification page
      router.push('/sign-in/otp');
    } catch (error: any) {
      toast.toast({
        title: 'Login failed',
        description: error.message || 'Failed to send OTP. Please try again.',
        variant: 'destructive',
      });
    }
  };

  const { isDirty, isValid } = loginForm.formState;
  return (
    <div>
      <Form {...loginForm}>
        <form onSubmit={loginForm.handleSubmit(onSubmit)}>
          <FormItem className="flex flex-col gap-5">
            <FormField
              control={loginForm.control}
              name="phone"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Your phone number</FormLabel>
                  <FormControl>
                    <PhoneInput
                      countries={['GH']}
                      defaultCountry="GH"
                      {...field}
                      placeholder="Enter you phone number"
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </FormItem>
          <Button
            className="mt-4 block w-full rounded-full bg-motitoBlue"
            variant="default"
            size="lg"
            loading={authStore.isSigningIn}
            disabled={!isDirty || !isValid || authStore.isSigningIn}
            type="submit"
          >
            Continue
          </Button>
        </form>
      </Form>
    </div>
  );
});

export default LoginForm;
